import { useMemo, useState } from "react";

import Box from "../Box";

import styled from "styled-components";
import BaseDashboardCard from "../BaseDashboardCard";
import useBreakPoint from "../../utils/useBreakPoint";
import { Choice } from "../../types/commercial";
import SelectOption from "../SelectOption";
import { SingleValue } from "react-select";
import { CText } from "@carrefour/design-system-components-react";
import { useSearchParams } from "react-router-dom";
import { CasseByRayon } from "../../types/operational";
import PaginationCard from "../PaginationCard";
import { eventTrack } from "../../utils/gtag";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "../../utils/numberUtils";
import { useRange } from "../../context/rangeContext";
import { Range } from "../../utils/range";

type ShrinkageByRayonCardProps = {
  title: string;
  listValue: CasseByRayon[];
  isUnavailable: boolean;
  isLoading: boolean;
  openInformationModal: (title?: string) => void;
  range: string;
  options: Choice[];
  importedDateTime?: string;
};

const Text = styled(CText)`
  font-family: system-ui;
`;

const Table = styled.table`
  width: 100%;
  align-items: center;
  border-collapse: collapse;
`;
const Tr = styled.tr``;
const TrCustom = styled.tr<{ isLast: boolean }>`
  border-collapse: collapse;
  border-bottom: ${(props) => (props?.isLast ? "" : " 1px solid #f0f3f6")};
`;
const Th = styled.th<{ $textAlign?: string }>`
  text-align: ${(props) => props?.$textAlign};
`;
const Td = styled.td<{ $textAlign?: string }>`
  text-align: ${(props) => props?.$textAlign};
  padding-block: 8px;
`;

const SelectBox = styled(Box)`
  width: 60%;
  @media only screen and (max-width: 600px) {
    width: 100%;
  }
`;
const ShrinkageByRayonCard = ({
  title,
  listValue,
  options,
  range,
  importedDateTime,
  ...res
}: ShrinkageByRayonCardProps) => {
  const isBreak = useBreakPoint();
  const [page, setPage] = useState(1);
  const n = 5;
  const totalPage = Math.ceil(listValue?.length / n);
  const [searchParams, setSearchParams] = useSearchParams();
  const { getKpiRange, setKpiRange } = useRange();

  const getDefaultValueParams = () => {
    const value =  getKpiRange(range) ?? options?.[0]?.value as string
    searchParams.set(range, value);
    setSearchParams(searchParams);
    // first tracking
    eventTrack({
      eventName: "interaction_metric",
      actionName: "filter applied",
      key1: "metric_name",
      value1: title,
      key2: "filter_name",
      value2: value,
    });
    return value;
  };

  const rangeParam = searchParams.get(range) ?? getDefaultValueParams();

  const handleChange = (choice: SingleValue<Choice>) => {
    const newValue = choice?.value as string;

    if (newValue === rangeParam) {
      return;
    }
    searchParams.set(range, newValue as string);
    setSearchParams(searchParams);
    setKpiRange(range, newValue as Range)
    // first tracking
    eventTrack({
      eventName: "interaction_metric",
      actionName: "filter applied",
      key1: "metric_name",
      value1: title,
      key2: "filter_name",
      value2: newValue,
    });
  };

  const filterData = useMemo(() => {
    return listValue?.filter((_, index) => {
      return index >= (page - 1) * n && index < page * n;
    });
  }, [listValue, page]);

  const nextPage = () => {
    setPage((old) => old + 1);
  };
  const prevPage = () => {
    setPage((old) => old - 1);
  };
  const lastPage = () => {
    setPage(totalPage);
  };
  const firstPage = () => {
    setPage(1);
  };

  const {t} = useTranslation();
  
  return (
    <BaseDashboardCard
      hasModal={true}
      title={title}
      option={
        <SelectBox py={1}>
          <SelectOption
            list={options}
            value={rangeParam}
            handleChange={handleChange}
          />
        </SelectBox>
      }
      size={"big"}
      importedDateTime={importedDateTime}
      {...res}
    >
      <Box display={"flex"} justifyContent={"center"} justifyItems={"center"}>
        <Box
          bg={"white"}
          display={"flex"}
          flexDirection={"column"}
          alignItems="center"
          justifyContent={"center"}
          width="100%"
          borderRadius={1}
          py={2}
        >
          <Table style={{ width: "100%", display: "table" }}>
            <thead style={{ width: "100%", display: "table" }}>
              <Tr>
                <Th $textAlign={"left"}>
                  <Text bold size={"m"} tagStyle={"p"}>
                      {t('operationnel.famille')}
                  </Text>
                </Th>
                <Th $textAlign={isBreak === "phone" ? "right" : "right"}>
                  <Text bold size={"m"} tagStyle={"p"}>
                    {t('operationnel.montant')}
                  </Text>
                </Th>
              </Tr>
            </thead>
            <Box
              width={"100%"}
              display={"flex"}
              height={listValue?.length < 5 ? "auto" : "220px"}
              flexDirection={"column"}
            >
              <tbody style={{ width: "100%", display: "table" }}>
                {filterData &&
                  filterData?.length > 0 &&
                  filterData?.map((item, index) => {
                    return subElements(
                      item,
                      isBreak,
                      filterData?.length === index + 1 && totalPage === page
                    );
                  })}
              </tbody>
            </Box>
          </Table>
          {totalPage > 1 && (
            <Box display="flex" justifyContent={"end"} width={"90%"}>
              <PaginationCard
                current={page}
                prevPage={prevPage}
                nextPage={nextPage}
                totalPage={totalPage}
                lastPage={lastPage}
                firstPage={firstPage}
                totalItems={listValue?.length}
                currentItems={filterData?.length}
              />
            </Box>
          )}
        </Box>
      </Box>
    </BaseDashboardCard>
  );
};

function subElements(item: CasseByRayon, isBreak: string, isLast: boolean) {
  return (
    <TrCustom key={"top-rec" + item.departmentKey} isLast={isLast}>
      <Td $textAlign={"left"}>
        <Box display={"flex"}>
          <Text size={"m"}>{item.departmentKey}</Text>
          &nbsp;
          <Text size={"m"}>{":"}</Text>
          &nbsp;
          <Text size={"m"}>{item.departmentDesc}</Text>
        </Box>
      </Td>
      <Td $textAlign={isBreak === "phone" ? "right" : "right"}>
        <Text bold>
          {item.shrinkageAmount ? `${formatCurrency(item.shrinkageAmount)}` : "-"}
        </Text>
      </Td>
    </TrCustom>
  );
}

export default ShrinkageByRayonCard;
